import { useState, useEffect } from "react";
import {
    Modal,
    Platform,
    ActivityIndicator,
    ScrollView,
    View,
    Alert,
    Switch,
    TouchableOpacity
} from "react-native";
import styled from "styled-components/native";
import { CheckBox } from "react-native-elements";
import {
    collection,
    doc,
    getDocs,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    arrayRemove,
    arrayUnion,
    serverTimestamp,
    where,
    query,
    orderBy,
} from "firebase/firestore";
import Button from "../../../components/Button";
import { firestore } from "../../../services/firebase";
import { HelperText } from "react-native-paper";
import TextInput from "../../../components/TextInput";
import {
    FooterText,
    StandardText,
    SmallText
} from "../../../config/theme/globalStyles";
import { Octicons } from '@expo/vector-icons'

export default function AdminFiltersModal({ visible, onClose }) {
    const [loadingTree, setLoadingTree] = useState(true);
    const [saving, setSaving] = useState(false);

    const [settings, setSettings] = useState({
        prefilterByTagFirst: false,
        filterByUserProducts: false
    });

    const [tree, setTree] = useState([]);
    const [newName, setNewName] = useState({ value: "", error: "" });
    const [newOrder, setNewOrder] = useState({ value: "", error: "" });
    const [showCreate, setShowCreate] = useState(false);

    const [filtersList, setFiltersList] = useState([]);
    const [editingFilter, setEditingFilter] = useState(null);
    const [associatedCats, setAssociatedCats] = useState([]);
    const [associatedCards, setAssociatedCards] = useState([]);
    const [expandedFilterId, setExpandedFilterId] = useState(null)

    // reset form
    function resetForm() {
        setNewName({ value: "", error: "" });
        setNewOrder({ value: "", error: "" });
        setShowCreate(false);
        setTree(old =>
            old.map(cat => ({
                ...cat,
                selected: false,
                cards: cat.cards.map(cd => ({ ...cd, selected: false }))
            }))
        );
    }

    useEffect(() => {
        if (!visible) return;
        (async () => {
            setLoadingTree(true);
            // fetch general settings
            try {
                const settingsRef = doc(firestore, "customization", "settings");
                const settingsSnap = await getDoc(settingsRef);
                if (settingsSnap.exists()) {
                    const data = settingsSnap.data();
                    setSettings({
                        prefilterByTagFirst: data.prefilterByTagFirst ?? false,
                        filterByUserProducts: data.filterByUserProducts ?? false
                    });
                }
            } catch (err) {
                console.error("Erro ao buscar settings:", err);
            }
            // fetch categories+cards tree, já ordenando
            try {
                const catsSnap = await getDocs(
                    query(
                        collection(firestore, "categories"),
                        orderBy("order", "asc")
                    )
                );
                const tmp = [];
                for (let catDoc of catsSnap.docs) {
                    const cat = {
                        id: catDoc.id,
                        title: catDoc.data().title,
                        selected: false,
                        cards: []
                    };
                    const cardsSnap = await getDocs(
                        query(
                            collection(firestore, "categories", cat.id, "cards"),
                            orderBy("order", "asc")
                        )
                    );
                    cardsSnap.docs.forEach(cdoc => {
                        cat.cards.push({
                            id: cdoc.id,
                            title: cdoc.data().title,
                            selected: false
                        });
                    });
                    tmp.push(cat);
                }
                setTree(tmp);
            } catch (err) {
                console.error("Erro ao carregar categorias:", err);
            }
            // fetch existing filters
            try {
                const q = query(
                    collection(firestore, 'filters'),
                    orderBy('order', 'asc')
                );
                const snap = await getDocs(q);
                const clean = snap.docs.map(docSnap => {
                    const d = docSnap.data();
                    return {
                        id: docSnap.id,
                        name: d.name,
                        slug: d.slug,
                        order: d.order,
                        archived: d.archived
                    }; s
                });

                setFiltersList(clean);
            } catch (err) {
                console.error('Erro ao buscar filtros:', err);
            } finally {
                setLoadingTree(false);
            }
        })();
    }, [visible]);

    // Quando o admin seleciona um filtro, busca categorias e cards associados
    useEffect(() => {
        if (!editingFilter) return;
        (async () => {
            // 1) ler o próprio documento do filtro
            const fRef = doc(firestore, 'filters', editingFilter.id);
            const fSnap = await getDoc(fRef);
            if (!fSnap.exists()) return;

            const { categoryIds = [], cardEntries = [] } = fSnap.data();

            // 2) buscar títulos das categorias
            const cats = await Promise.all(
                categoryIds.map(async catId => {
                    const cSnap = await getDoc(doc(firestore, 'categories', catId));
                    return { id: catId, title: cSnap.data()?.title ?? '' };
                })
            );
            setAssociatedCats(cats);

            // 3) buscar títulos dos cards
            const cards = await Promise.all(
                cardEntries.map(async ({ categoryId, cardId }) => {
                    const cardSnap = await getDoc(
                        doc(firestore, 'categories', categoryId, 'cards', cardId)
                    );
                    return {
                        categoryId,
                        cardId,
                        title: cardSnap.data()?.title ?? ''
                    };
                })
            );
            setAssociatedCards(cards);
        })();
    }, [editingFilter]);

    // arquivar / desarquivar
    async function toggleArchive() {
        const ref = doc(firestore, "filters", editingFilter.id);
        await updateDoc(ref, { archived: !editingFilter.archived });
        setFiltersList(list =>
            list.map(f =>
                f.id === editingFilter.id ? { ...f, archived: !f.archived } : f
            )
        );
        setEditingFilter(f => ({ ...f, archived: !f.archived }));
    }

    async function handleDeleteFilter(filterId) {
        try {
            const filterRef = doc(firestore, 'filters', filterId);
            const filterSnap = await getDoc(filterRef);
            if (!filterSnap.exists()) return;

            const { slug, categoryIds = [], cardEntries = [] } = filterSnap.data();
            const promises = [];

            // remover slug de cada categoria existente
            for (const catId of categoryIds) {
                const catRef = doc(firestore, 'categories', catId);
                const catSnap = await getDoc(catRef);
                if (catSnap.exists()) {
                    promises.push(
                        updateDoc(catRef, { filterIds: arrayRemove(slug) })
                    );
                }
            }

            // remover slug de cada card existente
            for (const { categoryId, cardId } of cardEntries) {
                const cardRef = doc(firestore, 'categories', categoryId, 'cards', cardId);
                const cardSnap = await getDoc(cardRef);
                if (cardSnap.exists()) {
                    promises.push(
                        updateDoc(cardRef, { filterIds: arrayRemove(slug) })
                    );
                }
            }

            // aguarda todas as remoções válidas
            await Promise.all(promises);

            // por fim, deleta o próprio filtro
            await deleteDoc(filterRef);

            // atualiza estado local
            setFiltersList(list => list.filter(f => f.id !== filterId));
            setEditingFilter(null);
        } catch (err) {
            Alert.alert('Erro', 'Não foi possível excluir o filtro.');
        }
    }

    async function handleSaveAll() {
        const trimmedName = newName.value.trim();

        if (!trimmedName) {
            setSaving(true);
            try {
                await setDoc(
                    doc(firestore, "customization", "settings"),
                    { ...settings, updatedAt: serverTimestamp() },
                    { merge: true }
                );
                onClose();
            } catch (e) {
                console.error("Erro ao salvar settings:", e);
            } finally {
                setSaving(false);
            }
            return;
        }

        const nameRegex = /^[\p{L}0-9]+(?: [\p{L}0-9]+){0,2}$/u;

        if (!nameRegex.test(trimmedName)) {
            setNewName({ ...newName, error: "Digite até 3 palavras sem caracteres especiais." });
            return;
        }
        // prevent duplicate
        const existsQ = query(
            collection(firestore, "filters"),
            where("name", "==", trimmedName)
        );
        const existsSnap = await getDocs(existsQ);
        if (!existsSnap.empty) {
            setNewName({ ...newName, error: "Já existe um filtro com esse nome." });
            return;
        }
        const orderNum = parseInt(newOrder.value, 10);
        if (isNaN(orderNum)) {
            setNewOrder({ ...newOrder, error: "Informe um número válido para a ordem." });
            return;
        }

        setSaving(true);
        try {
            // save general settings
            await setDoc(
                doc(firestore, "customization", "settings"),
                { ...settings, updatedAt: serverTimestamp() },
                { merge: true }
            );
            // create new filter
            const slug = trimmedName
                // 1) separa letras + seus acentos
                .normalize("NFD")
                // 2) remove os diacríticos (acentos)
                .replace(/[\u0300-\u036f]/g, "")
                // 3) abaixa tudo
                .toLowerCase()
                // 4) espaços viram underscore
                .replace(/\s+/g, "_")
                // 5) remove qualquer coisa que não seja letra a–z, número ou _
                .replace(/[^a-z0-9_]/g, "");

            // extrai só as categorias marcadas
            const selectedCategoryIds = tree
                .filter(cat => cat.selected)
                .map(cat => cat.id);

            // extrai só os cards marcados (dentro de cada categoria não totalmente marcada)
            const selectedCardEntries = tree
                .flatMap(cat =>
                    cat.selected
                        ? []       // se toda a categoria já foi marcada, não precisamos repetir os cards
                        : cat.cards
                            .filter(card => card.selected)
                            .map(card => ({
                                categoryId: cat.id,
                                cardId: card.id,
                            }))
                );

            const filterRef = doc(collection(firestore, "filters"));
            await setDoc(filterRef, {
                name: trimmedName,
                slug,
                order: orderNum,
                archived: false,
                createdAt: serverTimestamp(),
                categoryIds: selectedCategoryIds,
                cardEntries: selectedCardEntries
            }, { merge: true });
            // associate filter with selected categories/cards
            const batch = [];
            tree.forEach(cat => {
                if (cat.selected) {
                    batch.push(
                        updateDoc(doc(firestore, "categories", cat.id), {
                            filterIds: arrayUnion(slug)
                        })
                    );
                } else {
                    cat.cards.forEach(card => {
                        if (card.selected) {
                            batch.push(
                                updateDoc(
                                    doc(firestore, "categories", cat.id, "cards", card.id),
                                    { filterIds: arrayUnion(slug) }
                                )
                            );
                        }
                    });
                }
            });
            await Promise.all(batch);
            resetForm();
            onClose();
        } catch (e) {
            Alert.alert("Erro", "Não foi possível salvar os filtros. Tente novamente.");
        } finally {
            setSaving(false);
        }
    }

    function handleCancel() {
        resetForm();
        onClose();
    }

    if (!visible) return null;

    return (
        <Modal visible={visible} transparent animationType="slide">
            <Overlay>
                <Container behavior={Platform.OS === "ios" ? "padding" : undefined}>
                    <ScrollView style={{ padding: 20 }}>
                        {loadingTree ? (
                            <ActivityIndicator size="large" />
                        ) : (
                            <>
                                <StandardText>Configurações Gerais dos Filtros</StandardText>
                                <Section>
                                    <SmallText textAlign="left">
                                        Pré-filtragem ao abrir o app:
                                    </SmallText>
                                    <CheckBox
                                        title="Selecionar o primeiro filtro"
                                        checked={settings.prefilterByTagFirst}
                                        onPress={() =>
                                            setSettings(s => ({
                                                ...s,
                                                prefilterByTagFirst: true
                                            }))
                                        }
                                    />
                                    <CheckBox
                                        title="Exibir catálogo completo"
                                        checked={!settings.prefilterByTagFirst}
                                        onPress={() =>
                                            setSettings(s => ({
                                                ...s,
                                                prefilterByTagFirst: false
                                            }))
                                        }
                                    />
                                    <FooterText textAlign="left" color="black">
                                        {settings.prefilterByTagFirst
                                            ? "Resultado: Catálogo pré-filtrado com o primeiro filtro."
                                            : "Resultado: Catálogo completo sem filtro inicial."}
                                    </FooterText>
                                </Section>

                                <Section>
                                    <SmallText textAlign="left">
                                        Filtrar por produtos do aluno:
                                    </SmallText>
                                    <CheckBox
                                        title="Filtrar por produtos do aluno"
                                        checked={settings.filterByUserProducts}
                                        onPress={() =>
                                            setSettings(s => ({
                                                ...s,
                                                filterByUserProducts: true
                                            }))
                                        }
                                    />
                                    <CheckBox
                                        title="Não filtrar por produtos"
                                        checked={!settings.filterByUserProducts}
                                        onPress={() =>
                                            setSettings(s => ({
                                                ...s,
                                                filterByUserProducts: false
                                            }))
                                        }
                                    />
                                    <FooterText textAlign="left" color="black">
                                        {settings.filterByUserProducts
                                            ? "Resultado: Aluno verá apenas os módulos para os quais têm acesso."
                                            : "Resultado: Todos os módulos serão exibidos, independentemente do acesso do aluno."}
                                    </FooterText>
                                </Section>

                                {filtersList.length > 0 && (
                                    <Section>
                                        <StandardText>Gerenciar filtros existentes</StandardText>

                                        <ScrollView style={{ maxHeight: 200 }}>

                                            {filtersList.map(filter => {
                                                const isExpanded = expandedFilterId === filter.id
                                                const isPublished = !filter.archived

                                                return (
                                                    <View key={filter.id} style={{ marginBottom: 16 }}>
                                                        {/* cabeçalho da linha */}
                                                        <Row>
                                                            <TouchableOpacity
                                                                onPress={() => {
                                                                    if (isExpanded) {
                                                                        setExpandedFilterId(null)
                                                                        setEditingFilter(null)
                                                                    } else {
                                                                        setExpandedFilterId(filter.id)
                                                                        setEditingFilter(filter)
                                                                    }
                                                                }}
                                                            >
                                                                <Octicons
                                                                    name={isExpanded ? 'chevron-down' : 'chevron-right'}
                                                                    size={20}
                                                                    color="#333"
                                                                />
                                                            </TouchableOpacity>

                                                            <SmallText textAlign="left" style={{ marginHorizontal: 8 }}>
                                                                {filter.name}
                                                            </SmallText>

                                                            <SmallText textAlign="left" style={{ marginHorizontal: 8, fontWeight: isPublished ? 'bold' : 'normal' }}>
                                                                {isPublished ? 'Publicado' : 'Arquivado'}
                                                            </SmallText>

                                                            <Switch
                                                                value={isPublished}
                                                                onValueChange={async () => {
                                                                    setEditingFilter(filter)
                                                                    await toggleArchive()
                                                                }}
                                                            />
                                                        </Row>

                                                        {/* área expandida */}
                                                        {isExpanded && (
                                                            <Expanded>
                                                                <SmallText textAlign="left" style={{ marginBottom: 8 }}>
                                                                    Conteúdos vinculados:
                                                                </SmallText>
                                                                {/* categorias */}
                                                                {associatedCats.length > 0 && associatedCats.map(c => (
                                                                    <SmallText textAlign="left" key={c.id}>• Categoria: {c.title}</SmallText>
                                                                ))
                                                                }
                                                                {/* cards */}
                                                                {associatedCards.length > 0 && associatedCards.map(c => (
                                                                    <SmallText textAlign="left" key={c.cardId}>• Módulo: {c.title}</SmallText>
                                                                ))
                                                                }

                                                                <View style={{ marginTop: 12, alignItems: 'flex-start' }}>
                                                                    <Button
                                                                        title="Excluir"
                                                                        onPress={() => handleDeleteFilter(filter.id)}
                                                                        colorbutton="#a6a5a4"
                                                                        colortitle="black"
                                                                    />
                                                                </View>
                                                            </Expanded>
                                                        )}
                                                    </View>
                                                )
                                            })}
                                        </ScrollView>
                                    </Section>
                                )}

                                <Button
                                    title={showCreate ? "Cancelar criação de filtro" : "Criar novo filtro"}
                                    onPress={() => setShowCreate(v => !v)}
                                    disabled={loadingTree || saving}
                                    margin="0 0.5rem"
                                />
                                {showCreate && (
                                    <>
                                        <Section>
                                            <TextInput
                                                color="black"
                                                backgroundColor="white"
                                                label="Nome do Filtro"
                                                placeholder="Ex: Escola"
                                                value={newName.value}
                                                onChangeText={text =>
                                                    setNewName({ value: text, error: "" })
                                                }
                                                error={!!newName.error}
                                            />
                                            <HelperText type="error" visible={!!newName.error}>
                                                {newName.error}
                                            </HelperText>

                                            <TextInput
                                                color="black"
                                                backgroundColor="white"
                                                label="Ordem"
                                                placeholder="Nº da ordem que o filtro deve aparecer"
                                                value={newOrder.value}
                                                keyboardType="numeric"
                                                onChangeText={text => {
                                                    const digits = text.replace(/[^0-9]/g, "");
                                                    setNewOrder({ value: digits, error: "" });
                                                }}
                                                error={!!newOrder.error}
                                            />
                                            <HelperText type="error" visible={!!newOrder.error}>
                                                {newOrder.error}
                                            </HelperText>
                                        </Section>

                                        <Section>
                                            <SmallText textAlign="left">
                                                Vincular a Categorias ou Módulos:
                                            </SmallText>
                                            <ScrollView style={{ maxHeight: 200 }}>
                                                {tree.map(cat => (
                                                    <View key={cat.id}>
                                                        <CheckBox
                                                            title={"Categoria: " + cat.title}
                                                            checked={cat.selected}
                                                            onPress={() => {
                                                                setTree(t =>
                                                                    t.map(c =>
                                                                        c.id === cat.id
                                                                            ? {
                                                                                ...c,
                                                                                selected: !c.selected,
                                                                                cards: c.cards.map(cd => ({
                                                                                    ...cd,
                                                                                    selected: !c.selected
                                                                                }))
                                                                            }
                                                                            : c
                                                                    )
                                                                );
                                                            }}
                                                        />
                                                        {cat.cards.map(cd => (
                                                            <CheckBox
                                                                key={cd.id}
                                                                title={"  • " + cd.title}
                                                                checked={cd.selected}
                                                                onPress={() => {
                                                                    setTree(t =>
                                                                        t.map(c =>
                                                                            c.id === cat.id
                                                                                ? {
                                                                                    ...c,
                                                                                    cards: c.cards.map(cc =>
                                                                                        cc.id === cd.id
                                                                                            ? { ...cc, selected: !cc.selected }
                                                                                            : cc
                                                                                    )
                                                                                }
                                                                                : c
                                                                        )
                                                                    );
                                                                }}
                                                                containerStyle={{ backgroundColor: "transparent", paddingVertical: 0 }}
                                                                textStyle={{ marginLeft: 16, fontSize: 14 }}
                                                            />
                                                        ))}
                                                    </View>
                                                ))}
                                            </ScrollView>
                                        </Section>
                                    </>
                                )}

                                <ButtonsRow>
                                    <Button
                                        title="Cancelar"
                                        onPress={handleCancel}
                                        margin="0rem 0.5rem"
                                        colorbutton="#D9D9D9"
                                        colortitle="black"
                                    />
                                    <Button
                                        title={"Salvar"}
                                        onPress={handleSaveAll}
                                        isLoading={saving}
                                    />
                                </ButtonsRow>
                            </>
                        )}
                    </ScrollView>
                </Container>
            </Overlay>
        </Modal>
    );
}

// estilos
const Overlay = styled.View`
  flex: 1;
  background-color: rgba(0,0,0,0.6);
  justify-content: center;
  align-items: center;
`;
const Container = styled.KeyboardAvoidingView`
  width: 90%;
  max-height: 90%;
  background: #fff;
  border-radius: 12px;
`;
const Section = styled.View`
  margin-bottom: 24px;
`;
const ButtonsRow = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 16px;
`;

const Row = styled.View`
  flex-direction: row;
  align-items: center;
  padding-vertical: 8px;
`

const Expanded = styled.View`
  background: #FAFAFA;
  padding: 12px;
  border-radius: 4px;
  margin-top: 8px;
  border: 1px solid #E0E0E0;
`
